import { useI18n } from "@/context/I18nContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/common/EmptyState";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine,
    PieChart,
    Pie,
    AreaChart,
    Area,
} from "recharts";
import {
    Wallet,
    Percent,
    TrendingUp,
    TrendingDown,
    Receipt,
    Trophy,
    Award,
    Smartphone,
    ArrowUpRight,
    ArrowDownRight,
    PieChart as PieIcon,
    LineChart as LineIcon,
    Lightbulb,
    Coins,
    AlertTriangle,
    Moon,
} from "lucide-react";
import { OPS } from "@/constants/testIds";
import { fmtNumber, fmtCompact } from "@/lib/format";
import {
    groupLabel,
    groupColor,
    finCurrency,
    isRamadan,
    FIN_PROFIT,
    FIN_LOSS,
    FIN_SALES,
    FIN_GROUP_ORDER,
} from "@/lib/finLabels";

// ------------------------------------------------------------------ helpers
const AXIS_TICK = { fontSize: 11, fill: "var(--ink-muted)" };
const GRID_COLOR = "var(--border-soft)";

const money0 = (v, lang) =>
    v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 0)} ${finCurrency(lang)}`;
const money2 = (v, lang) =>
    v === null || v === undefined ? "\u2014" : `${fmtNumber(v, lang, 2)} ${finCurrency(lang)}`;

const pctChange = (cur, prev) => {
    if (cur === null || cur === undefined || prev === null || prev === undefined || prev === 0)
        return null;
    return ((cur - prev) / Math.abs(prev)) * 100;
};

const kpiVal = (kpi, key) => {
    const c = (kpi?.cards || []).find((x) => x.key === key);
    return c ? c.value : null;
};

// ------------------------------------------------------------------ shells
function StoryCard({ icon: Icon, title, caption, right, children, testId, className = "" }) {
    return (
        <Card
            data-testid={testId}
            className={`fin-fade-up surface border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-200 ease-out ${className}`}
        >
            <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
                <div className="flex items-start gap-2.5">
                    {Icon ? (
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[color:rgba(209,128,47,0.12)]">
                            <Icon className="h-[18px] w-[18px] text-[color:var(--brand-brown)]" />
                        </span>
                    ) : null}
                    <div>
                        <CardTitle className="text-lg sm:text-xl font-bold text-[color:var(--ink)]">
                            {title}
                        </CardTitle>
                        {caption ? (
                            <p className="mt-0.5 text-xs sm:text-sm text-[color:var(--ink-muted)]">{caption}</p>
                        ) : null}
                    </div>
                </div>
                {right ? <div className="shrink-0">{right}</div> : null}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

function DeltaPill({ value, unit = "percent", higherIsBetter = true, testId }) {
    const { t, lang } = useI18n();
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const up = value >= 0;
    const good = higherIsBetter ? up : !up;
    const Arrow = up ? ArrowUpRight : ArrowDownRight;
    const color = good ? FIN_PROFIT : FIN_LOSS;
    const bg = good ? "rgba(22,163,74,0.10)" : "rgba(220,38,38,0.10)";
    const num = `${up ? "+" : ""}${fmtNumber(value, lang, 1)}${unit === "points" ? ` ${t("fin_pts")}` : "%"}`;
    return (
        <span
            data-testid={testId}
            className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
            style={{ color, background: bg }}
        >
            <Arrow className="h-3.5 w-3.5" />
            <bdi dir="ltr">{num}</bdi>
            <span className="font-normal text-[color:var(--ink-muted)]">{t("fin_vs_last_month")}</span>
        </span>
    );
}

// ================================================================== 1. HERO
export function HeroWaterfall({ kpi, expenses, loading, isSingleMonth }) {
    const { t, lang } = useI18n();

    const revenue = kpiVal(kpi, "revenue") || 0;
    const net = kpi?.net_value != null ? kpi.net_value : kpiVal(kpi, "net") || 0;
    const groups = expenses?.groups || [];
    const dataReady = (kpi?.data_available || expenses?.data_available) && (revenue || net || groups.length);

    // Build the descending money-flow staircase: Sales -> each cost -> kept.
    const ordered = FIN_GROUP_ORDER.map((k) => groups.find((g) => g.key === k)).filter(Boolean);
    const extras = groups.filter((g) => !FIN_GROUP_ORDER.includes(g.key));
    const costs = [...ordered, ...extras];

    const rows = [];
    rows.push({ key: "sales", label: t("fin_sales_label"), base: 0, value: revenue, amount: revenue, color: FIN_SALES, kind: "sales" });
    let running = revenue;
    costs.forEach((g) => {
        const amt = g.amount || 0;
        const base = running - amt;
        rows.push({
            key: g.key,
            label: groupLabel(g.key, lang),
            base,
            value: amt,
            amount: amt,
            share: g.share,
            color: groupColor(g.key),
            kind: "cost",
            isKey: g.key === "app_commission",
        });
        running = base;
    });
    const keptPositive = net >= 0;
    rows.push({
        key: "kept",
        label: t("fin_kept_label"),
        base: keptPositive ? 0 : net,
        value: Math.abs(net),
        amount: net,
        color: keptPositive ? FIN_PROFIT : FIN_LOSS,
        kind: "kept",
    });

    const chartHeight = Math.max(280, rows.length * 46 + 24);

    const HeroTip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        const share = revenue ? (Math.abs(d.amount) / revenue) * 100 : null;
        return (
            <div
                dir={lang === "ar" ? "rtl" : "ltr"}
                className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs"
            >
                <p className="font-semibold text-[color:var(--ink)]">{d.label}</p>
                <p className="tnum text-[color:var(--ink)]">{money2(d.amount, lang)}</p>
                {share != null && d.kind !== "sales" ? (
                    <p className="tnum text-[color:var(--ink-muted)]">
                        {fmtNumber(share, lang, 1)}% {t("fin_of_sales")}
                    </p>
                ) : null}
            </div>
        );
    };

    return (
        <StoryCard
            testId={OPS.financials.hero}
            icon={Coins}
            title={t("fin_hero_title")}
            caption={t("fin_hero_sub")}
            className="relative overflow-hidden"
        >
            {/* decorative overlay (<20% viewport, brand sky/orange) */}
            <div
                className="pointer-events-none absolute inset-0 -z-0"
                style={{
                    background:
                        "radial-gradient(680px circle at 15% 0%, rgba(186,217,235,0.45), transparent 55%), radial-gradient(560px circle at 90% 0%, rgba(209,128,47,0.14), transparent 60%)",
                }}
                aria-hidden="true"
            />
            <div className="relative">
                {loading ? (
                    <Skeleton className="h-[300px] rounded-xl" />
                ) : !dataReady ? (
                    <EmptyState icon={Coins} title={t("financials_empty")} dataset="financials_pl_monthly" />
                ) : (
                    <>
                        {/* Kept headline */}
                        <div className="mb-4 flex flex-wrap items-end gap-x-3 gap-y-1">
                            <span className="text-sm text-[color:var(--ink-muted)]">{t("fin_kept_label")}</span>
                            <span
                                data-testid={OPS.financials.heroKept}
                                className="kpi-number text-3xl sm:text-4xl font-extrabold tnum"
                                style={{ color: keptPositive ? FIN_PROFIT : FIN_LOSS }}
                            >
                                {money0(net, lang)}
                            </span>
                            <span
                                className="inline-flex items-center gap-1 text-sm font-semibold"
                                style={{ color: keptPositive ? FIN_PROFIT : FIN_LOSS }}
                            >
                                {keptPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                                {keptPositive ? t("fin_profit_word") : t("fin_loss_word")}
                            </span>
                        </div>

                        <div dir="ltr" data-testid={OPS.financials.heroWaterfall}>
                            <ResponsiveContainer width="100%" height={chartHeight}>
                                <BarChart
                                    data={rows}
                                    layout="vertical"
                                    barCategoryGap="22%"
                                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} horizontal={false} />
                                    <XAxis type="number" tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID_COLOR }} />
                                    <YAxis
                                        type="category"
                                        dataKey="label"
                                        width={lang === "ar" ? 132 : 150}
                                        tick={{ fontSize: 12, fill: "var(--ink)" }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <ReferenceLine x={0} stroke="var(--border-strong)" />
                                    <Tooltip cursor={{ fill: "rgba(186,217,235,0.18)" }} content={<HeroTip />} />
                                    <Bar dataKey="base" stackId="a" fill="transparent" isAnimationActive={false} />
                                    <Bar dataKey="value" stackId="a" radius={[6, 6, 6, 6]} maxBarSize={30}>
                                        {rows.map((r) => (
                                            <Cell key={r.key} fill={r.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </>
                )}
            </div>
        </StoryCard>
    );
}

// ===================================================== 2. HEADLINE CARDS
export function HeadlineCards({ kpi, monthlySeries, selectedMonthYM, isSingleMonth, loading }) {
    const { t, lang } = useI18n();

    const revenue = kpiVal(kpi, "revenue");
    const expense = kpiVal(kpi, "expense");
    const net = kpi?.net_value != null ? kpi.net_value : kpiVal(kpi, "net");
    const margin = kpiVal(kpi, "margin");

    // Previous month (single-month selection only) for delta arrows.
    const months = monthlySeries?.months || [];
    let prev = null;
    if (isSingleMonth && selectedMonthYM) {
        const idx = months.findIndex((m) => m.month === selectedMonthYM);
        if (idx > 0) prev = months[idx - 1];
    }
    const prevMargin = prev && prev.revenue ? (prev.net / prev.revenue) * 100 : null;

    const cards = [
        {
            key: "kept",
            title: isSingleMonth ? t("fin_card_kept_month") : t("fin_card_kept_period"),
            icon: Wallet,
            value: money0(net, lang),
            color: net != null && net < 0 ? FIN_LOSS : FIN_PROFIT,
            delta: prev ? pctChange(net, prev.net) : null,
            unit: "percent",
            higherIsBetter: true,
        },
        {
            key: "per100",
            title: t("fin_card_per100"),
            icon: Percent,
            value: margin == null ? "\u2014" : `${fmtNumber(margin, lang, 1)} ${finCurrency(lang)}`,
            color: "var(--ink)",
            delta: prev && prevMargin != null && margin != null ? margin - prevMargin : null,
            unit: "points",
            higherIsBetter: true,
        },
        {
            key: "revenue",
            title: t("fin_card_revenue"),
            icon: TrendingUp,
            value: money0(revenue, lang),
            color: "var(--ink)",
            delta: prev ? pctChange(revenue, prev.revenue) : null,
            unit: "percent",
            higherIsBetter: true,
        },
        {
            key: "cost",
            title: t("fin_card_cost"),
            icon: Receipt,
            value: money0(expense, lang),
            color: "var(--ink)",
            delta: prev ? pctChange(expense, prev.expense) : null,
            unit: "percent",
            higherIsBetter: false,
        },
    ];

    if (loading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-[118px] rounded-2xl" />
                ))}
            </div>
        );
    }

    return (
        <div data-testid={OPS.financials.kpiGrid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {cards.map((c) => {
                const Icon = c.icon;
                return (
                    <div
                        key={c.key}
                        data-testid={OPS.financials.kpiStoryCard(c.key)}
                        className="fin-fade-up surface border border-[color:var(--border-soft)] rounded-2xl shadow-[var(--shadow-sm)] p-4 sm:p-5 transition-[box-shadow,transform] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]"
                    >
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-[color:var(--ink-muted)]">
                            <Icon className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            <span className="leading-snug">{c.title}</span>
                        </div>
                        <p className="kpi-number mt-2 text-2xl sm:text-3xl font-extrabold tnum" style={{ color: c.color }}>
                            {c.value}
                        </p>
                        <DeltaPill value={c.delta} unit={c.unit} higherIsBetter={c.higherIsBetter} testId={OPS.financials.kpiDelta(c.key)} />
                    </div>
                );
            })}
        </div>
    );
}

// ===================================================== 3. BRANCH LEADERBOARD
export function BranchLeaderboard({ branches, selectedBranchId, onSelectBranch, loading }) {
    const { t, tn, lang } = useI18n();
    const rows = branches?.rows || [];
    const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net || 0)));

    return (
        <StoryCard
            testId={OPS.financials.leaderboard}
            icon={Trophy}
            title={t("fin_leaderboard_title")}
            caption={`${t("fin_leaderboard_sub")} · ${t("fin_tap_branch")}`}
        >
            {loading ? (
                <Skeleton className="h-[320px] rounded-xl" />
            ) : !rows.length ? (
                <EmptyState icon={Trophy} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => {
                        const selected = selectedBranchId === r.branch.id;
                        const w = `${(Math.abs(r.net || 0) / maxAbs) * 100}%`;
                        const color = r.is_loss ? FIN_LOSS : FIN_PROFIT;
                        return (
                            <button
                                key={r.branch.id}
                                data-testid={OPS.financials.leaderboardRow(r.branch.id)}
                                onClick={() => onSelectBranch(r.branch.id)}
                                className={`group w-full min-h-[44px] rounded-xl border px-3 py-2.5 text-start transition-[box-shadow,transform,background-color] duration-150 ease-out hover:bg-[color:rgba(186,217,235,0.18)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(186,217,235,0.9)] ${
                                    selected
                                        ? "border-[color:var(--brand-orange)] bg-[color:rgba(209,128,47,0.06)]"
                                        : "border-[color:var(--border-soft)]"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="flex items-center gap-2 text-sm font-medium text-[color:var(--ink)] truncate">
                                        {tn(r.branch)}
                                        {selected ? (
                                            <Badge variant="secondary" className="bg-[color:rgba(209,128,47,0.16)] text-[color:var(--brand-brown)] border-0 text-[10px] px-1.5 py-0">
                                                {t("fin_selected")}
                                            </Badge>
                                        ) : null}
                                        {r.partial ? (
                                            <Badge variant="secondary" className="bg-[color:rgba(209,128,47,0.14)] text-[color:var(--brand-brown)] border-0 text-[10px] px-1.5 py-0">
                                                {t("partial_short")}
                                            </Badge>
                                        ) : null}
                                    </span>
                                    <span className="tnum text-sm font-bold shrink-0" style={{ color }}>
                                        {money0(r.net, lang)}
                                    </span>
                                </div>
                                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[color:rgba(224,222,217,0.7)]">
                                    <div className="h-full rounded-full transition-[width] duration-300 ease-out" style={{ width: w, background: color }} />
                                </div>
                                {r.is_loss ? (
                                    <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: FIN_LOSS }}>
                                        <AlertTriangle className="h-3.5 w-3.5" />
                                        {t("fin_losing_tag")}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            )}
        </StoryCard>
    );
}

// ===================================================== 4. EXPENSE DESTINATION
export function ExpenseDestination({ expenses, loading }) {
    const { t, lang } = useI18n();
    const groups = expenses?.groups || [];
    const total = expenses?.total;
    const pieData = groups.map((g) => ({ key: g.key, name: groupLabel(g.key, lang), value: g.amount, share: g.share, color: groupColor(g.key) }));

    const Tip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div dir={lang === "ar" ? "rtl" : "ltr"} className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs">
                <p className="font-semibold text-[color:var(--ink)]">{d.name}</p>
                <p className="tnum text-[color:var(--ink)]">{money2(d.value, lang)}</p>
                {d.share != null ? <p className="tnum text-[color:var(--ink-muted)]">{fmtNumber(d.share, lang, 1)}%</p> : null}
            </div>
        );
    };

    return (
        <StoryCard testId={OPS.financials.expenseDest} icon={PieIcon} title={t("fin_expenses_title")} caption={t("fin_expenses_sub")}>
            {loading ? (
                <Skeleton className="h-[280px] rounded-xl" />
            ) : !groups.length ? (
                <EmptyState icon={PieIcon} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <div className="space-y-4">
                    <div dir="ltr" className="mx-auto w-full max-w-[260px]">
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="88%" paddingAngle={2} stroke="var(--bg-surface)" strokeWidth={2}>
                                    {pieData.map((d) => (
                                        <Cell key={d.key} fill={d.color} stroke={d.key === "app_commission" ? "var(--ink)" : "var(--bg-surface)"} strokeWidth={2} />
                                    ))}
                                </Pie>
                                <Tooltip content={<Tip />} />
                            </PieChart>
                        </ResponsiveContainer>
                        {total != null ? (
                            <p className="text-center text-xs text-[color:var(--ink-muted)] -mt-1">
                                {t("fin_card_cost")}: <span className="tnum font-semibold text-[color:var(--ink)]">{money0(total, lang)}</span>
                            </p>
                        ) : null}
                    </div>
                    <div className="space-y-2">
                        {groups.map((g) => {
                            const isKey = g.key === "app_commission";
                            return (
                                <div
                                    key={g.key}
                                    data-testid={OPS.financials.expenseDestRow(g.key)}
                                    className={`rounded-xl border px-3 py-2.5 ${isKey ? "border-[color:#0EA5A4] bg-[color:rgba(14,165,164,0.06)]" : "border-[color:var(--border-soft)] surface-2"}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="flex min-w-0 items-center gap-2 text-sm text-[color:var(--ink)]">
                                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: groupColor(g.key) }} />
                                            <span className={isKey ? "font-bold" : ""}>{groupLabel(g.key, lang)}</span>
                                            {isKey ? (
                                                <Badge data-testid={OPS.financials.commissionBadge} variant="secondary" className="border-0 text-[10px] px-1.5 py-0 whitespace-nowrap" style={{ background: "rgba(14,165,164,0.16)", color: "#0E7490" }}>
                                                    {t("fin_key_drain")}
                                                </Badge>
                                            ) : null}
                                        </span>
                                        <span className="flex items-center gap-2 text-xs tnum shrink-0">
                                            <span className="font-medium text-[color:var(--ink)]">{money0(g.amount, lang)}</span>
                                            <span className="min-w-[42px] text-end font-semibold text-[color:var(--brand-brown)]">{fmtNumber(g.share, lang, 1)}%</span>
                                        </span>
                                    </div>
                                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[color:rgba(224,222,217,0.7)]">
                                        <div className="h-full rounded-full" style={{ width: `${g.share || 0}%`, background: groupColor(g.key) }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </StoryCard>
    );
}

// ===================================================== 5. PROFIT TREND
export function ProfitTrend({ monthlySeries, loading }) {
    const { t, lang } = useI18n();
    const months = monthlySeries?.months || [];
    const rows = months.map((m) => ({
        ym: m.month,
        label: `${(lang === "ar" ? m.label_ar : m.label_en).split(" ")[0]} ${m.month.slice(2, 4)}`,
        full: lang === "ar" ? m.label_ar : m.label_en,
        net: m.net,
        partial: m.partial,
        ramadan: isRamadan(m.month),
    }));
    const anyRamadan = rows.some((r) => r.ramadan);

    const MonthTick = ({ x, y, payload }) => {
        const d = rows[payload.index];
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={12} textAnchor="middle" fill="var(--ink-muted)" fontSize={10}>
                    {payload.value}
                </text>
                {d?.partial ? <circle cx={-7} cy={20} r={2.6} fill={FIN_SALES} /> : null}
                {d?.ramadan ? <circle cx={7} cy={20} r={2.6} fill="#7FA8C9" /> : null}
            </g>
        );
    };

    const Tip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        if (!d) return null;
        return (
            <div dir={lang === "ar" ? "rtl" : "ltr"} className="rounded-lg border border-[color:var(--border-soft)] surface px-3 py-2 shadow-[var(--shadow-md)] text-xs">
                <p className="mb-1 flex items-center gap-1.5 font-semibold text-[color:var(--ink)]">
                    {d.full}
                    {d.partial ? (
                        <span className="rounded px-1 text-[10px]" style={{ background: "rgba(209,128,47,0.16)", color: "var(--brand-brown)" }}>
                            {t("partial_short")}
                        </span>
                    ) : null}
                    {d.ramadan ? (
                        <span className="rounded px-1 text-[10px]" style={{ background: "rgba(186,217,235,0.6)", color: "#0E7490" }}>
                            {t("fin_badge_ramadan")}
                        </span>
                    ) : null}
                </p>
                <p className="tnum" style={{ color: d.net < 0 ? FIN_LOSS : FIN_PROFIT }}>
                    {money2(d.net, lang)}
                </p>
            </div>
        );
    };

    const legend = (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-[color:var(--ink-muted)]">
            <span className="inline-flex items-center gap-1">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: FIN_SALES }} /> {t("partial_short")}
            </span>
            {anyRamadan ? (
                <span data-testid={OPS.financials.ramadanBadge} className="inline-flex items-center gap-1">
                    <Moon className="h-3.5 w-3.5" style={{ color: "#7FA8C9" }} /> {t("fin_badge_ramadan")}
                </span>
            ) : null}
        </div>
    );

    return (
        <StoryCard testId={OPS.financials.profitTrend} icon={LineIcon} title={t("fin_trend_title")} caption={t("fin_trend_sub")} right={legend}>
            {loading ? (
                <Skeleton className="h-[300px] rounded-xl" />
            ) : !rows.length ? (
                <EmptyState icon={LineIcon} title={t("financials_empty")} dataset="financials_pl_monthly" />
            ) : (
                <>
                    <div dir="ltr">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 22 }}>
                                <defs>
                                    <linearGradient id="finNetFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={FIN_PROFIT} stopOpacity={0.22} />
                                        <stop offset="100%" stopColor={FIN_PROFIT} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                                <XAxis dataKey="label" tick={<MonthTick />} interval="preserveStartEnd" minTickGap={8} tickLine={false} axisLine={{ stroke: GRID_COLOR }} height={42} />
                                <YAxis tickFormatter={fmtCompact} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                                <ReferenceLine y={0} stroke="var(--border-strong)" />
                                <Tooltip cursor={{ stroke: "var(--border-strong)", strokeWidth: 1 }} content={<Tip />} />
                                <Area type="monotone" dataKey="net" stroke={FIN_PROFIT} strokeWidth={2.5} fill="url(#finNetFill)" dot={false} activeDot={{ r: 4 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    <p className="mt-2 text-xs text-[color:var(--ink-muted)]">{t("fin_partial_not_final")}</p>
                </>
            )}
        </StoryCard>
    );
}

// ===================================================== 6. INSIGHTS STRIP
export function InsightsStrip({ kpi, expenses, branches, monthlySeries, isSingleMonth, loading }) {
    const { t, tn, lang } = useI18n();
    const periodWord = isSingleMonth ? t("fin_this_month") : t("fin_this_period");
    const items = [];

    const comm = (expenses?.groups || []).find((g) => g.key === "app_commission");
    if (comm && comm.amount) {
        items.push({ key: "commission", icon: Smartphone, text: `${t("fin_insight_commission")} ${money0(comm.amount, lang)} ${periodWord}` });
    }

    const bRows = branches?.rows || [];
    if (bRows.length) {
        const losers = bRows.filter((r) => r.is_loss).length;
        if (losers === 1) items.push({ key: "losers", icon: TrendingDown, text: `${t("fin_insight_branch_lost_one")} ${periodWord}` });
        else if (losers > 1) items.push({ key: "losers", icon: TrendingDown, text: `${fmtNumber(losers, lang, 0)} ${t("fin_insight_branches_lost")} ${periodWord}` });
    }

    const months = monthlySeries?.months || [];
    if (months.length) {
        const best = months.reduce((a, b) => (b.net > a.net ? b : a), months[0]);
        items.push({ key: "best", icon: Trophy, text: `${t("fin_best_month")}: ${lang === "ar" ? best.label_ar : best.label_en}` });
    }

    if (bRows.length && bRows[0].net > 0) {
        items.push({ key: "top", icon: Award, text: `${t("fin_top_branch")}: ${tn(bRows[0].branch)}` });
    }

    const margin = kpiVal(kpi, "margin");
    if (margin != null) {
        items.push({ key: "margin", icon: Percent, text: `${t("fin_card_per100")} ${fmtNumber(margin, lang, 1)} ${finCurrency(lang)}` });
    }

    if (loading) {
        return (
            <div className="flex flex-wrap gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-[52px] w-[220px] rounded-xl" />
                ))}
            </div>
        );
    }
    if (!items.length) return null;

    return (
        <StoryCard testId={OPS.financials.insights} icon={Lightbulb} title={t("fin_insights_title")}>
            <div className="flex flex-wrap gap-3">
                {items.map((it) => {
                    const Icon = it.icon;
                    return (
                        <div
                            key={it.key}
                            data-testid={OPS.financials.insightItem(it.key)}
                            className="fin-fade-up flex items-center gap-2.5 rounded-xl border border-[color:var(--border-soft)] surface-2 px-3.5 py-2.5"
                        >
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[color:rgba(209,128,47,0.12)]">
                                <Icon className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            </span>
                            <span className="text-sm font-medium text-[color:var(--ink)]">{it.text}</span>
                        </div>
                    );
                })}
            </div>
        </StoryCard>
    );
}
